import Keyboard from 'react-simple-keyboard';
import React, {
    Dispatch,
    MutableRefObject,
    ReactNode,
    useCallback,
    useEffect,
    useMemo,
    useState,
} from 'react';
import {useOrientation} from './orientation';

export type KeyboardTuple = [MutableRefObject<HTMLInputElement | HTMLTextAreaElement>, string, (value: string) => void];
type KeyboardLayout = 'shift' | 'default' | 'shift-persisted' | 'alt' | 'numpad' | 'numpad-extended' | 'numpad-special-extended' | 'numpad-with-minus';

export function useKeyboard(defaultLayout: KeyboardLayout = 'shift', persistLayout = false): [Dispatch<KeyboardTuple>, () => ReactNode, KeyboardTuple] {
    const [keyboardRef, setKeyboardRef] = useState<Keyboard>(null);
    const [keyboardLayout, setKeyboardLayoutRaw] = useState(defaultLayout);
    const [currentInput, setCurrentInput] = useState<KeyboardTuple>(null);
    const orientation = useOrientation();
    const setKeyboardLayout = useCallback((value: KeyboardLayout) => {
        if (persistLayout) {
            return;
        }

        setKeyboardLayoutRaw(value);
    }, [persistLayout]);

    const keyboardVisible = useMemo(() => !!currentInput, [currentInput]);

    const setActiveInput = useCallback((activeInput: KeyboardTuple) => {
        if (orientation === 'none') {
            return;
        }

        if (activeInput && activeInput[1].length > 0) {
            setKeyboardLayout('default');
        } else {
            setKeyboardLayout('shift');
        }
        setCurrentInput(activeInput);
    }, [setKeyboardLayout, orientation]);

    const onChange = useCallback((content: string) => {
        currentInput[2](content);
    }, [currentInput]);

    const onKeyPress = useCallback((key: string) => {
        if (key === '{enter}') {
            currentInput[0].current.blur();
            return;
        }

        let layoutName = null;

        if (keyboardLayout === 'shift' && !(key.includes('{') && key.includes('}'))) {
            layoutName = 'default';
        }

        switch (key) {
            case '{shift}':
            case '{shift-activated}':
            case '{shift-persisted}':
            case '{default}':
                if (keyboardLayout === 'default') {
                    layoutName = 'shift';
                } else if (keyboardLayout === 'shift') {
                    layoutName = 'shift-persisted';
                } else {
                    layoutName = 'default';
                }
                break;
            case '{alt}':
            case '{alt-right}':
                layoutName = keyboardLayout === 'alt' ? 'default' : 'alt';
                break;

            default:
                break;
        }

        if (layoutName) {
            setKeyboardLayout(layoutName);
        }
    }, [setKeyboardLayout, currentInput, keyboardLayout]);

    const layout = useMemo(() => {
        return {
            default: [
                '1 2 3 4 5 6 7 8 9 0',
                'q w e r t y u ı o p ğ ü {backspace}',
                'a s d f g h j k l ş i ,',
                '{shift} < z x c v b n m ö ç . {shift}',
                '{alt} {space} {enter}',
            ],
            shift: [
                '1 2 3 4 5 6 7 8 9 0',
                'Q W E R T Y U I O P Ğ Ü {backspace}',
                'A S D F G H J K L Ş İ ;',
                '{shift-activated} > Z X C V B N M Ö Ç : {shift-activated}',
                '{alt} {space} {enter}',
            ],
            'shift-persisted': [
                '1 2 3 4 5 6 7 8 9 0',
                'Q W E R T Y U I O P Ğ Ü {backspace}',
                'A S D F G H J K L Ş İ ;',
                '{shift-persisted} > Z X C V B N M Ö Ç : {shift-persisted}',
                '{alt} {space} {enter}',
            ],
            alt: [
                '1 2 3 4 5 6 7 8 9 0 {backspace}',
                `@ # $ & * ( ) ' "`,
                '% - + = / ; : ! ?',
                '{default} {space} {enter}',
            ],
            numpad: [
                '1 2 3',
                '4 5 6',
                '7 8 9',
                '{empty} 0 {backspace}',
            ],
            'numpad-extended': [
                '1 2 3',
                '4 5 6',
                '7 8 9',
                '{empty} - 0 . : {backspace}',
            ],
            'numpad-special-extended': [
                '1 2 3',
                '4 5 6',
                '7 8 9',
                ': 0 {backspace}',
            ] ,'numpad-with-minus': [
                '1 2 3',
                '4 5 6',
                '7 8 9',
                '- 0 {backspace}',
            ],
        };

        // todo orientation

    }, []);

    const renderKeyboard = useCallback(() => renderIf(keyboardVisible)(() => (
        <div className={'keyboard ' + orientation}>
            <Keyboard
                maxLength={currentInput ? (currentInput[0].current.maxLength) : 20}
                keyboardRef={setKeyboardRef}
                preventMouseDownDefault={true}
                onChange={onChange}
                onKeyPress={onKeyPress}
                theme='hg-theme-default hg-theme-ios'
                layout={layout}
                display={{
                    '{alt}': '.?123',
                    '{shift}': '⇧',
                    '{shift-activated}': '⇧',
                    '{shift-persisted}': '⇪',
                    '{enter}': 'TAMAM',
                    '{backspace}': '⌫',
                    '{space}': ' ',
                    '{default}': 'abc',
                    '{empty}': ' ',
                }}
                layoutName={keyboardLayout}
            />
        </div>
    )), [currentInput, keyboardLayout, keyboardVisible, layout, onChange, onKeyPress, orientation]);

    useEffect(() => {
        if (keyboardRef && currentInput) {
            setKeyboardLayout("default")
            keyboardRef.setInput(currentInput[1]);
        }
    }, [keyboardRef, currentInput]);

    return [setActiveInput, renderKeyboard, currentInput];
}
